import React from 'react';
import {
  Flex, Spacer, Text, useColorModeValue, HStack,
} from '@chakra-ui/react';
import { useHistory } from 'react-router-dom';
import DashPanel from './DashPanel';
import { useRecurringRevenues, usePendingRecurringEnrollments } from '../../hooks/useRecurringRevenues';
import { RecurringRevenueStatus, monthlyRecurringValue } from '../../models/recurringRevenue';

// dart (2026-07-15, Chad) — NEXT_EPIC #4: a universe-dash roster of every client with a LIVE recurring
// subscription — what they're subscribed to + the monthly amount, one click to the project. MRR stays
// on its own KPI; this is the "quick go-see-them" list.
const money = (n: number): string => `$${(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const RecurringSubscriptionsCard: React.FC = () => {
  const history = useHistory();
  const { data } = useRecurringRevenues();
  const subColor = useColorModeValue('gray.500', 'whiteAlpha.600');
  const labelColor = useColorModeValue('gray.800', 'whiteAlpha.900');
  const rowHover = useColorModeValue('blackAlpha.50', 'whiteAlpha.100');
  const divider = useColorModeValue('blackAlpha.100', 'whiteAlpha.200');

  const rows = (data?.index ?? []).filter(
    (r) => !r.isArchive && !r.isVoid && r.status !== RecurringRevenueStatus.Cancelled,
  );
  const total = rows.reduce((s, r) => s + monthlyRecurringValue(r), 0);
  // item 763 (Chad) — the Subscriptions-page KPI trio on the dashboard too: active monthly,
  // pending-to-enroll monthly (the value sold-but-unenrolled deals would bill), combined.
  const { data: pendingData } = usePendingRecurringEnrollments();
  const pendingMonthly = (pendingData?.index ?? [])
    .filter((r) => !r.declined)
    .reduce((s2, r) => s2 + (r.monthlyValue ?? 0), 0);
  const pendingCount = (pendingData?.index ?? []).filter((r) => !r.declined).length;

  return (
    <DashPanel label="Recurring subscriptions" accent="purple.400">
      {/* item 763 — active vs pending-to-enroll vs total, at a glance */}
      {/* item 778 — Chakra 1.6's Flex has NO gap prop (silently ignored → the three stats ran
          together). HStack spacing is the 1.6-era way. */}
      <HStack spacing={10} mb={3} pb={3} borderBottomWidth="1px" borderColor={divider} align="flex-start" flexWrap="wrap">
        <Flex direction="column">
          <Text fontSize="xs" color={subColor} textTransform="uppercase" letterSpacing="0.05em">Active</Text>
          <Text fontSize="md" fontWeight="bold" color={labelColor}>{`${money(total)}/mo`}</Text>
        </Flex>
        <Flex direction="column">
          <Text fontSize="xs" color={subColor} textTransform="uppercase" letterSpacing="0.05em">Pending enrollment</Text>
          <Text fontSize="md" fontWeight="bold" color={pendingMonthly > 0 ? 'orange.300' : labelColor}>
            {`${money(pendingMonthly)}/mo`}
            {pendingCount > 0 ? ` · ${pendingCount}` : ''}
          </Text>
        </Flex>
        <Flex direction="column">
          <Text fontSize="xs" color={subColor} textTransform="uppercase" letterSpacing="0.05em">Total</Text>
          <Text fontSize="md" fontWeight="bold" color={labelColor}>{`${money(total + pendingMonthly)}/mo`}</Text>
        </Flex>
      </HStack>
      {rows.length === 0 && (
        <Text fontSize="sm" color={subColor}>No active subscriptions yet.</Text>
      )}
      <Flex direction="column">
        {rows.map((r) => (
          <Flex
            key={r.id}
            align="center"
            gap={3}
            py={2.5}
            px={2}
            mx={-2}
            borderRadius="lg"
            cursor="pointer"
            transition="background 0.15s"
            _hover={{ bg: rowHover }}
            onClick={() => history.push(`/project/${r.projectId}`)}
          >
            <Flex direction="column" minW={0}>
              <Text fontSize="sm" fontWeight="medium" color={labelColor} noOfLines={1}>
                {r.customerName || 'Customer'}
              </Text>
              <Text fontSize="xs" color={subColor} noOfLines={1}>
                {r.name}
                {r.status === RecurringRevenueStatus.Paused ? ' · Paused' : ''}
              </Text>
            </Flex>
            <Spacer />
            <Text fontSize="sm" fontWeight="semibold" color={labelColor} whiteSpace="nowrap">
              {`${money(monthlyRecurringValue(r))}/mo`}
            </Text>
          </Flex>
        ))}
      </Flex>
      {rows.length > 0 && (
        <Flex mt={2} pt={2} borderTopWidth="1px" borderColor={divider}>
          <Text fontSize="xs" color={subColor}>{`${rows.length} active`}</Text>
          <Spacer />
          <Text fontSize="sm" fontWeight="bold" color={labelColor}>{`${money(total)}/mo total`}</Text>
        </Flex>
      )}
    </DashPanel>
  );
};

export default RecurringSubscriptionsCard;
